import React, { useState, useEffect } from 'react';
import { Container, Table, Button } from 'react-bootstrap';
import { ParkInput } from '@models/Models';
import useParkInputs from '@hooks/useParkInputs';
import { ParkInputStatus } from '@models/Enums';
import MyButton from '@components/MyButton';
import useExcel from '@hooks/useExcel';

const ITEMS_PER_PAGE = 10; // Set the number of items per page

const LoadingZones = () => {
    const [filteredParkInputs, setFilteredParkInputs] = useState<ParkInput[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);

    const { getAllParkInputs } = useParkInputs();
    const { exportAllInputs } = useExcel();

    useEffect(() => {
        fetchInputs(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchInputs = async (page: number) => {
        const offset = page * ITEMS_PER_PAGE;
        const response = await getAllParkInputs(ITEMS_PER_PAGE, offset);

        if (!response.success) {
            console.error('Failed to fetch park inputs:', response.message);
            alert(response.message);
            return;
        }

        setFilteredParkInputs(response.data);
        setTotalItems(response.total as number);
        setCurrentPage(page);
    };

    const handleNextPage = () => {
        if ((currentPage + 1) * ITEMS_PER_PAGE < totalItems) {
            fetchInputs(currentPage + 1);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 0) {
            fetchInputs(currentPage - 1);
        }
    };

    const handleExportToExcel = async () => {
        try {
            setLoading(true);

            await exportAllInputs();
        } catch (error) {
            console.error('Failed to export to Excel:', error);
            alert('Fehler beim Exportieren in Excel');
        }finally {
            setLoading(false);
        }
    };

    return (
        <Container fluid className='myCont'>
            <div className="d-flex flex-row gap-2 py-2 justify-content-between">
                <div>
                    <h1>Ladezone-Belegung</h1>
                    <p>Klicken Sie auf „Alle anzeigen“, wenn Sie auch abgeschlossene Eingaben sehen möchten.</p>
                </div>
                <div className='d-flex flex-row align-items-center gap-3'>
                    <MyButton
                        title={loading ? "Loading..." : "Exportieren in Excel"}
                        disabled={filteredParkInputs.length === 0}
                        isSecundary={false}
                        onClick={handleExportToExcel}
                    />
                </div>
            </div>

            {/* Pagination Controls */}
            <div className="d-flex justify-content-between my-2">
                <Button disabled={currentPage === 0} onClick={handlePrevPage}>
                    ⬅️ Vorherige Seite
                </Button>
                <span>Seite {currentPage + 1}</span>
                <Button disabled={(currentPage + 1) * ITEMS_PER_PAGE >= totalItems} onClick={handleNextPage}>
                    Nächste Seite ➡️
                </Button>
            </div>

            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Projekt</th>
                        <th>SMS 1</th>
                        <th>SMS 2</th>
                        <th>Abfahrt</th>
                        <th>Foto</th>
                        <th>Ladezone</th>
                        <th>KFZ KZ</th>
                        <th>Adresse</th>
                        <th>Haus nr.</th>
                        <th>Stiege</th>
                        <th>TOP nr.</th>
                        <th>Telefon</th>
                        <th>Unterschrift</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredParkInputs.map((item) => (
                        <tr key={item.$id} style={{ borderWidth: 3, borderColor: item.status === ParkInputStatus.CONFLICT ? 'red' : item.status === ParkInputStatus.SECOND_SMS_SENT ? 'orange' : 'transparent' }}>
                            <td>{item.$id}</td>
                            <td>{item.project?.name}</td>
                            <td>{item.firstSMSsentAt ? new Date(item.firstSMSsentAt).toLocaleString() : ''}</td>
                            <td>{item.secondSMSsendAt ? new Date(item.secondSMSsendAt).toLocaleString() : ''}</td>
                            <td>{item.endetAt ? new Date(item.endetAt).toLocaleString() : ''}</td>
                            <td><img loading='lazy' src={item.image} alt="Bild" width={50} height={50} style={{ maxWidth: 50 }} /></td>
                            <td>{item.loadingZone}</td>
                            <td>{item.vehiclePlate}</td>
                            <td>{item.address?.street}</td>
                            <td>{item.address?.hausnummer}</td>
                            <td>{item.address?.stiege}</td>
                            <td>{item.topNummer}</td>
                            <td>{item.phoneNumber}</td>
                            <td><img loading='lazy' src={item.signature} alt="Bild" width={50} height={50} style={{ maxWidth: 50 }} /></td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Container>
    );
};

export default LoadingZones;
